<template>
  <el-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :title="t('addVendorBill')"
    width="1000px"
    @close="onClose"
  >
    <div>
      <el-form
        @submit.prevent="handleSubmit"
        novalidate
        :model="formData"
        :rules="rules"
        label-position="top"
        ref="formRef"
        class="flex flex-col items-center"
      >
        <!-- @submit errors -->
        <alert-badge
          isDanger
          :title="error.title"
          :body="error.body"
          @dismissed="
            () => {
              error.title = ''
              error.body = ''
            }
          "
        />

        <!-- Vendor Name -->
        <div class="input-section">
          <h1>{{ t('sections.relatedBill') }}</h1>

          <el-form-item :label="t('vendorName')" prop="vendorID" class="input-label">
            <el-select v-model="formData.vendorID" filterable class="w-full" required>
              <el-option
                v-for="v in vendors"
                :key="v.id"
                :label="v.name"
                :value="v.id"
              >
                <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                  {{ v.name }}
                </span>
              </el-option>
            </el-select>
          </el-form-item>
        </div>

        <!-- Bill Date, Due Date, Vendor Bill Number -->
        <div class="input-section">
          <div class="input-group">
            <el-form-item :label="t('billDate')" prop="billDate" class="input-label" style="margin: 5px auto">
              <el-date-picker
                v-model="formData.billDate"
                type="date"
                :placeholder="t('billDate')"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item :label="t('dueDate')" prop="billDueDate" class="input-label" style="margin: 5px auto">
              <el-date-picker
                v-model="formData.billDueDate"
                type="date"
                :placeholder="t('dueDate')"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item :label="t('vendorBillNumber')" prop="vendorBillNumber" class="input-label" style="margin: 5px auto">
              <el-input
                v-model="formData.vendorBillNumber"
                :placeholder="t('vendorBillNumber')"
                maxlength="100"
                clearable
              />
            </el-form-item>
          </div>
        </div>

        <!-- bill amount -->
        <div class="input-section">
          <el-form-item :label="t('totalAmount')" prop="totalDueAmount" class="input-label">
            <money-input
              :currency="currency"
              v-model="formData.totalDueAmount"
              @dinero-created="(dinero) => (totalDueDinero = dinero)"
              ref="totalAmountRef"
            />
          </el-form-item>
          <div class="input-group">
            <el-form-item :label="t('paidAmount')" prop="totalPaidAmount" class="input-label">
              <money-input
                :currency="currency"
                v-model="formData.totalPaidAmount"
                @dinero-created="(dinero) => (totalPaidDinero = dinero)"
                ref="paidAmountRef"
                :key="JSON.stringify(fullPaid)"
              />
            </el-form-item>
            <el-form-item class="input-label">
              <horizontal-button
                :title="t('fullyPaid')"
                :isSuccess="true"
                @click.prevent="fullPay"
                class="fully-paid-button"
              />
            </el-form-item>
          </div>
          <div class="w-full p-0">
            <el-form-item :label="t('paidFrom')" prop="requestOnlyPayFromAccountID" class="input-label">
              <el-select
                v-model="formData.requestOnlyPayFromAccountID"
                :trigger-on-focus="false"
                value-key="name"
                class="w-full"
                filterable
                remote
                :remote-method="searchAccounts"
              >
                <el-option
                  :key="registerAccount.id"
                  :label="$ctx.locale === 'ar' ? registerAccount.arName : registerAccount.enName"
                  :value="registerAccount.id"
                >
                  <div class="w-full bg-green-100">
                    {{ $ctx.locale === 'ar' ? registerAccount.arName : registerAccount.enName }}
                  </div>
                </el-option>

                <el-option
                  v-for="safe in safes"
                  :key="safe.id"
                  :label="$ctx.locale === 'ar' ? safe.arName : safe.enName"
                  :value="safe.id"
                >
                  <div class="w-full bg-green-100">
                    {{ $ctx.locale === 'ar' ? safe.arName : safe.enName }}
                  </div>
                </el-option>

                <el-option
                  v-for="bank in banks"
                  :key="bank.id"
                  :label="$ctx.locale === 'ar' ? bank.arName : bank.enName"
                  :value="bank.id"
                >
                  <div class="w-full bg-green-100">
                    {{ $ctx.locale === 'ar' ? bank.arName : bank.enName }}
                  </div>
                </el-option>

                <el-option
                  v-for="option in accounts"
                  :key="option.id"
                  :label="$ctx.locale === 'ar' ? option.arName : option.enName"
                  :value="option.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </div>
        </div>

        <!-- What's For and Debited Account -->
        <div class="input-section">
          <div class="input-group">
            <el-form-item :label="t('whatsFor')" prop="whatsFor" class="input-label" style="margin: 5px auto">
              <el-input
                v-model="formData.whatsFor"
                :placeholder="t('whatsFor')"
                maxlength="500"
                clearable
              />
            </el-form-item>

            <el-form-item :label="t('debitedAccount')" class="input-label" style="margin: 5px auto">
              <el-input
                :value="t('inventoryAccount')"
                disabled
                class="w-full"
              />
            </el-form-item>
          </div>
        </div>

        <!-- Notes -->
        <div class="input-section">
          <el-form-item :label="t('notes')" prop="notes" class="input-label">
            <el-input
              v-model="formData.notes"
              type="textarea"
              :placeholder="t('notes')"
              :rows="4"
              maxlength="1000"
              show-word-limit
            />
          </el-form-item>
        </div>

        <!-- Attachments -->
        <div class="input-section">
          <vendor-bill-attachment-input v-model="formData.attachmentFiles" />
        </div>
      </el-form>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="onClose">{{ t('cancel') }}</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="isSubmitting">
          {{ t('create') }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ref, reactive, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHTTP } from '@/plugins/http'
import { useContext } from '@/plugins/context'
import { Account } from '@/models/accounting/Account'
import Vendor from '@/models/vendor/Vendor'
import AlertBadge from '@/components/AlertBadge.vue'
import MoneyInput from '@/components/form/MoneyInput.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import VendorBillAttachmentInput from '@/components/file-manager/VendorBillAttachmentInput.vue'
import type { FormInstance, FormRules } from 'element-plus'

const props = defineProps<{
  modelValue: boolean
  inventoryReceivingID: string
  currency: string
  precision: number
  vendor: any
  debitedAccount: any
}>()

const emit = defineEmits(['update:modelValue', 'submit'])

const { t } = useI18n({
  messages: {
    en: {
      addVendorBill: 'Add Related Vendor Bill',
      sections: {
        relatedBill: 'Related Vendor Bill'
      },
      vendorName: 'Vendor Name',
      billDate: 'Bill Date',
      dueDate: 'Due Date',
      vendorBillNumber: 'Vendor Bill Number',
      totalAmount: 'Total Amount',
      paidAmount: 'Paid Amount',
      paidFrom: 'Paid from',
      whatsFor: "What's For",
      debitedAccount: 'Debited Account',
      inventoryAccount: 'Inventory account',
      notes: 'Notes',
      cancel: 'Cancel',
      create: 'Create Vendor Bill',
      vendorRequired: 'Vendor is required',
      totalAmountRequired: 'Total amount is required',
      billDateRequired: 'Bill date is required',
      payFromAccountRequired: 'Pay from account is required when making a payment',
      fullyPaid: 'Fully Paid',
      errYouMustSelectAnAccount: 'You must select an account to pay from'
    },
    ar: {
      addVendorBill: 'إضافة فاتورة مورد مرتبطة',
      sections: {
        relatedBill: 'فاتورة مورد مرتبطة'
      },
      vendorName: 'اسم المورد',
      billDate: 'تاريخ الفاتورة',
      dueDate: 'تاريخ الاستحقاق',
      vendorBillNumber: 'رقم فاتورة المورد',
      totalAmount: 'المبلغ الإجمالي',
      paidAmount: 'المبلغ المدفوع',
      paidFrom: 'حساب الدفع',
      whatsFor: 'الغرض',
      debitedAccount: 'الحساب المدين',
      inventoryAccount: 'حساب المخزون',
      notes: 'ملاحظات',
      cancel: 'إلغاء',
      create: 'إنشاء فاتورة المورد',
      vendorRequired: 'المورد مطلوب',
      totalAmountRequired: 'المبلغ الإجمالي مطلوب',
      billDateRequired: 'تاريخ الفاتورة مطلوب',
      payFromAccountRequired: 'حساب الدفع مطلوب عند إجراء الدفع',
      fullyPaid: 'دفع المبلغ الكامل',
      errYouMustSelectAnAccount: 'يجب اختيار حساب للدفع'
    }
  }
})

const http = useHTTP()
const ctx = useContext()
const formRef = ref<FormInstance>()
const error = ref({ title: '', body: '' })
const isSubmitting = ref(false)
const vendor = ref(props.vendor)
const vendors = ref<Vendor[]>([])
const accounts = ref<Account[]>([])
const registerAccount = ref(new Account())
const safes = ref<Account[]>([])
const banks = ref<Account[]>([])
const loadingAccounts = ref(false)
const totalDueDinero = ref()
const totalPaidDinero = ref()
const fullPaid = ref(false)
const totalAmountRef = ref()
const paidAmountRef = ref()

const formData = reactive({
  vendorID: props.vendor?.id || '',
  billDate: null as Date | null,
  billDueDate: null as Date | null,
  vendorBillNumber: '',
  totalDueAmount: 0,
  totalPaidAmount: 0,
  requestOnlyPayFromAccountID: '',
  whatsFor: '',
  notes: '',
  attachmentFiles: [] as any[]
})

const rules: FormRules = {
  vendorID: [{ required: true, message: t('vendorRequired'), trigger: 'change' }],
  billDate: [{ required: true, message: t('billDateRequired'), trigger: 'change' }],
  totalDueAmount: [{ required: true, message: t('totalAmountRequired'), trigger: 'blur' }],
  requestOnlyPayFromAccountID: [
    {
      validator: (_rule: any, value: any, callback: any) => {
        if (formData.totalPaidAmount > 0 && !value) {
          callback(new Error(t('payFromAccountRequired')))
        } else {
          callback()
        }
      },
      trigger: 'change'
    }
  ]
}

// Load initial data
onMounted(async () => {
  try {
    // Load vendors
    const vendorResp = await http.get<Vendor[]>(Vendor.ENDPOINT)
    vendors.value = vendorResp.map((v) => Vendor.from(v))

    // Load register account, safes, and banks
    registerAccount.value = Account.from(
      await http.get<Account>(`${Account.ENDPOINT}/${ctx.currentRegister.accountID}`)
    )
    const safesResp = await http.get<Account[]>('accounting/accounts/safes?skip_balance=true')
    safes.value = safesResp.map((s) => Account.from(s))
    const banksResp = await http.get<Account[]>('accounting/accounts/banks?skip_balance=true')
    banks.value = banksResp.map((b) => Account.from(b))
  } catch (err) {
    console.error('Error loading initial data:', err)
  }
})

async function searchAccounts(query: string) {
  loadingAccounts.value = true
  try {
    const url = `${Account.ENDPOINT}?q=${query || ''}`
    const resp = await http.get<Account[]>(url)
    accounts.value = resp.map((a) => Account.from(a))
  } catch (err) {
    console.error('Error searching accounts:', err)
  } finally {
    loadingAccounts.value = false
  }
}

function fullPay() {
  formData.totalPaidAmount = formData.totalDueAmount
  fullPaid.value = true
}

async function handleSubmit() {
  if (!formRef.value) return

  // Validation check for payment account
  if (formData.totalPaidAmount > 0 && !formData.requestOnlyPayFromAccountID) {
    error.value.title = t('errYouMustSelectAnAccount')
    error.value.body = ''
    return
  }

  try {
    await formRef.value.validate()
  } catch (err) {
    return
  }

  if (isSubmitting.value) return

  isSubmitting.value = true
  error.value = { title: '', body: '' }

  const payload = {
    vendorID: formData.vendorID,
    billDate: formData.billDate,
    billDueDate: formData.billDueDate,
    vendorBillNumber: formData.vendorBillNumber,
    totalDueAmount: formData.totalDueAmount,
    totalPaidAmount: formData.totalPaidAmount,
    currency: props.currency,
    precision: props.precision,
    requestOnlyPayFromAccountID: formData.requestOnlyPayFromAccountID || null,
    whatsFor: formData.whatsFor,
    notes: formData.notes,
    attachmentFiles: formData.attachmentFiles
  }

  emit('submit', payload)
}

function onClose() {
  // Reset form
  formData.vendorID = props.vendor?.id || ''
  formData.billDate = null
  formData.billDueDate = null
  formData.vendorBillNumber = ''
  formData.totalDueAmount = 0
  formData.totalPaidAmount = 0
  formData.requestOnlyPayFromAccountID = ''
  formData.whatsFor = ''
  formData.notes = ''
  formData.attachmentFiles = []
  fullPaid.value = false
  error.value = { title: '', body: '' }

  emit('update:modelValue', false)
}

// Update form when props change
watch(
  () => props.vendor,
  () => {
    vendor.value = props.vendor
    formData.vendorID = props.vendor?.id || ''
  }
)

// Reset isSubmitting when dialog closes
watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      isSubmitting.value = false
    }
  }
)
</script>

<style scoped>
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

h1 {
  @apply text-lg py-3 font-bold;
}

h2 {
  @apply py-3 font-bold;
}

h3 {
  @apply py-3 font-bold;
}

.input-group {
  @apply flex flex-col justify-between w-full items-end;
}

.input-group-row {
  @apply flex justify-between w-full;
}

.input-group-row > label {
  @apply w-6/12;
}

.input-section {
  @apply rounded w-full justify-center;
}

@screen lg {
  .input-section {
    @apply w-7/12;
  }
}

@screen md {
  .input-section {
    @apply flex-row;
  }

  .input-group {
    @apply flex-row;
  }

  .input-group > .input-label {
    @apply w-6/12;
  }
}

.remove-button {
  @apply text-lg;
  color: var(--danger-text-color);
  align-self: center;
  justify-self: center;
  cursor: pointer;
  margin: 0.5rem;
}

.remove-button:hover {
  color: var(--danger-hover-bg-color);
  align-self: center;
  justify-self: center;
}

.fully-paid-button {
  height: 40px;
}

hr.solid {
  border-top: 1px solid var(--tertiary-bg-color);
}

.hide {
  display: none;
}

.input:hover + .hide {
  display: flex;
  justify-content: center;
}

.danger {
  color: var(--danger-text-color);
  background: var(--danger-bg-color);
}

.success {
  color: var(--success-text-color);
  background: var(--success-bg-color);
}

td,
th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
  text-align: center;
}

tr:nth-child(even) {
  background-color: #f3f3f3;
}
</style>
